import type {
  DataTableElement,
  DisputeDraftCardElement,
  KpiStripElement,
  TextBlockElement,
} from 'src/demo-mode/demo-elements/types'
import type { DemoChatScript } from 'src/demo-mode/types'

import { ACT1_ERROR_TYPES, ACT1_EVIDENCE, ACT1_FILE } from '../data'

const usd = (n: number) => `$${n.toLocaleString('en-US')}`
const usd2 = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// ─────────── Beat 1 elements ───────────

const SCAN_KPIS: KpiStripElement = {
  kind: 'kpi-strip',
  id: 'ia-act1-kpis',
  tiles: [
    {
      label: 'Line items scanned',
      value: ACT1_FILE.lineItems.toLocaleString('en-US'),
    },
    {
      label: 'Lines flagged',
      value: ACT1_FILE.flaggedLines.toLocaleString('en-US'),
      sublabel: 'vs contracted rate card',
      status: 'warning',
    },
    {
      label: 'Recoverable',
      value: usd(ACT1_FILE.found),
      sublabel: 'this invoice',
      status: 'critical',
    },
  ],
}

const FINDINGS_TABLE: DataTableElement = {
  kind: 'data-table',
  id: 'ia-act1-findings',
  subtitle: `${ACT1_FILE.carrier} · ${ACT1_FILE.weekLabel} · ${usd(ACT1_FILE.billed)} billed`,
  columns: [
    { key: 'type', label: 'Discrepancy' },
    { key: 'count', label: 'Lines', align: 'right' },
    { key: 'amount', label: 'Overcharge', align: 'right' },
  ],
  rows: ACT1_ERROR_TYPES.map((e) => ({
    id: e.id,
    status: e.status,
    emphasize: e.id === ACT1_ERROR_TYPES[0].id,
    cells: {
      type: e.label,
      count: e.count.toLocaleString('en-US'),
      amount: usd(e.dollars),
    },
  })),
  takeaway: `${ACT1_FILE.flaggedLines} lines flagged · ${usd(ACT1_FILE.found)} recoverable, ranked by dollar.`,
}

const EVIDENCE_CLOSER: TextBlockElement = {
  kind: 'text-block',
  id: 'ia-act1-evidence-closer',
  text: `Top of the list is duplicate address-correction fees. Example — shipment ${ACT1_EVIDENCE.trackingNumber} was billed the ${ACT1_EVIDENCE.accessorialLabel.toLowerCase()} fee (${ACT1_EVIDENCE.accessorialCode}) at ${usd2(ACT1_EVIDENCE.billed)}; the rate card allows it once, so contract is ${usd2(ACT1_EVIDENCE.contract)}. That is a clean ${usd2(ACT1_EVIDENCE.delta)} credit. **Want the dispute filings drafted?**`,
}

// ─────────── Beat 2 elements ───────────

const DISPUTE_DRAFT: DisputeDraftCardElement = {
  kind: 'dispute-draft-card',
  id: 'ia-act1-dispute',
  carrier: ACT1_FILE.carrier,
  headerLabel: 'Account #•••4471 · Batch DR-2026-W24',
  // One filing = one error type + the one clause it cites. This is the
  // top filing (duplicate address-correction, the largest bucket).
  clauseCited:
    'FedEx Pricing Agreement §4.2 — address-correction billed once per shipment',
  lines: [
    {
      reference: ACT1_EVIDENCE.trackingNumber,
      detail: `${ACT1_EVIDENCE.accessorialLabel} (${ACT1_EVIDENCE.accessorialCode}) — 2nd occurrence`,
      billed: usd2(ACT1_EVIDENCE.billed),
      contract: usd2(ACT1_EVIDENCE.contract),
      credit: usd2(ACT1_EVIDENCE.delta),
    },
    {
      reference: '7789 4415 6642',
      detail: 'Address correction (ADDR-CORR) — 2nd occurrence',
      billed: '$20.10',
      contract: '$0.00',
      credit: '$20.10',
    },
    {
      reference: '7789 4419 8830',
      detail: 'Address correction (ADDR-CORR) — 2nd occurrence',
      billed: '$19.30',
      contract: '$0.00',
      credit: '$19.30',
    },
  ],
  footerSummary: `${ACT1_FILE.disputeDraftCount} filings · ${usd(ACT1_FILE.found)} · covering all ${ACT1_FILE.flaggedLines} flagged lines`,
}

const BEAT2_CLOSER: TextBlockElement = {
  kind: 'text-block',
  // `after-approvals` prefix → renders below the approval card.
  id: 'after-approvals-ia-act1-closer',
  text: `These ${ACT1_FILE.disputeDraftCount} filings are ready to send — approve and they go to ${ACT1_FILE.carrier}'s billing queue. That's one carrier, one week: ${usd(ACT1_FILE.found)}. **Want the last 90 days, across all four carriers?**`,
}

export const invoiceAudit: DemoChatScript = {
  id: 'invoice-audit',
  name: 'Invoice Audit — one invoice, one rate card',
  description:
    'Act 1: upload a FedEx weekly invoice + the contracted rate card; Neo audits 46K line items, ranks the overcharges, and drafts carrier-ready disputes in the same session.',
  triggers: [
    // Natural-language phrases that OPEN Act 1 from a fresh chat. Substring-
    // matched, case-insensitive — keep each phrase distinctive enough not to
    // fire by accident.
    'invoice',
    'rate card',
    'audit this',
    'audit our',
    'audit the',
    'check this against',
    'check our',
    'overcharge',
    'overbill',
    'billing error',
    'carrier bill',
    'parcel bill',
    'fedex bill',
    "here's last month",
    'last month',
    'reconcile',
  ],
  followUps: [
    {
      // End of Act 1 → Act 2 ("Want the last 90 days, across all four
      // carriers?"). Covers plain affirmatives AND topic words, because a
      // demoer may answer the closer either way ("yes" / "show me the 90
      // days" / "all carriers"). Generous by design: the only sensible next
      // step here IS Act 2, so a false-positive match is harmless.
      triggers: [
        // affirmatives
        'yes',
        'yeah',
        'yep',
        'yup',
        'sure',
        'ok',
        'okay',
        'go ahead',
        "let's",
        'lets',
        'do it',
        'please',
        'sounds good',
        'absolutely',
        'definitely',
        'show me',
        'let me see',
        'see it',
        'next',
        'continue',
        'keep going',
        'more',
        // topic words
        '90 day',
        'ninety day',
        'last 90',
        'all carrier',
        'all four',
        'every carrier',
        'across carrier',
        'other carrier',
        'recovery at scale',
        'at scale',
        'how big',
        'bigger picture',
        'full picture',
        'whole picture',
        'rest of',
        'last quarter',
        'the quarter',
        'network',
        'everything',
      ],
      nextScriptId: 'recovery-at-scale',
    },
  ],
  events: [
    // ─────────── Beat 1: parse + findings ───────────
    {
      type: 'status',
      delayMs: 200,
      message: `Parsing ${ACT1_FILE.lineItems.toLocaleString('en-US')} line items…`,
    },
    { type: 'thinking-start', delayMs: 200 },
    {
      type: 'thinking-delta',
      delayMs: 400,
      delta:
        'Matching every line to the rate card by service level and zone, then de-duplicating accessorials. The address-correction fees are the noisy bucket — many shipments billed twice.\n',
    },
    { type: 'thinking-stop', delayMs: 200 },
    { type: 'tool-call', delayMs: 200, name: 'parse_carrier_invoice' },
    { type: 'tool-call', delayMs: 300, name: 'match_rate_card' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: `Scanned ${ACT1_FILE.lineItems.toLocaleString('en-US')} lines against your ${ACT1_FILE.carrier} contract. ${ACT1_FILE.flaggedLines} don't match — ${usd(ACT1_FILE.found)} recoverable.`,
    },
    { type: 'demo-element-add', delayMs: 600, element: SCAN_KPIS },
    { type: 'demo-element-add', delayMs: 700, element: FINDINGS_TABLE },
    { type: 'demo-element-add', delayMs: 700, element: EVIDENCE_CLOSER },
    { type: 'done', delayMs: 400, title: 'Invoice audit — findings' },
    { type: 'await-user-prompt', delayMs: 0 },

    // ─────────── Beat 2: disputes drafted ───────────
    { type: 'tool-call', delayMs: 300, name: 'draft_carrier_disputes' },
    {
      type: 'text-delta',
      delayMs: 500,
      delta: `Drafted all ${ACT1_FILE.disputeDraftCount} filings.`,
    },
    {
      type: 'text-delta',
      delayMs: 500,
      delta:
        ' Each cites the tracking number, the contract clause, and the credit requested — every flagged line covered.',
    },
    { type: 'demo-element-add', delayMs: 600, element: DISPUTE_DRAFT },
    {
      type: 'approval',
      delayMs: 700,
      proposal: {
        proposalId: 'demo-invoice-audit-act1-dispute-top10',
        toolName: 'file_carrier_dispute',
        actionType: 'dispute_filing',
        description: `File ${ACT1_FILE.disputeDraftCount} dispute filings with ${ACT1_FILE.carrier} — ${usd(ACT1_FILE.found)} across ${ACT1_FILE.flaggedLines} line items on invoice ${ACT1_FILE.weekLabel}. Each filing cites the contracted rate and the overbilled amount.`,
      },
    },
    { type: 'demo-element-add', delayMs: 700, element: BEAT2_CLOSER },
    { type: 'done', delayMs: 400, title: 'Invoice audit — disputes filed' },
    {
      type: 'await-approval-resolved',
      delayMs: 0,
      proposalId: 'demo-invoice-audit-act1-dispute-top10',
    },
  ],
}
